"use client";

import { FC, useEffect } from "react";
import { useAuiState } from "@assistant-ui/react";
import { useTranslation } from "react-i18next";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useContextBudgetStore } from "@/components/custom/stores/contextBudgetStore";

const SIZE = 22;
const STROKE = 2.5;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * Branch-level context-budget donut. Subscribes to the assistant-ui active-path
 * projection; whenever the leaf message id changes (cold load, branch switch,
 * post-stream finalize), kicks a fetch to keep the snapshot fresh.
 *
 * Backend owns the math — we just render. Per ADR
 * <c>00X-context-budget-display-is-branch-scoped</c>.
 */
export const ContextBudgetDonut: FC = () => {
  const { t } = useTranslation();
  const threadId = useAuiState((s) => s.threadListItem.remoteId);
  // Subscribe only to the leaf message ID — not the full messages array.
  // Avoids re-rendering on every streaming token.
  const leafId = useAuiState((s) => {
    const m = s.thread.messages;
    return m?.length ? m[m.length - 1].id : undefined;
  });
  const snap = useContextBudgetStore((s) => (threadId ? s.byThread[threadId] : undefined));

  useEffect(() => {
    if (!threadId || !leafId) return;
    // FinishEvent capture covers the live-stream case; this fetch covers cold
    // load → hydrate (the envelope seeded the store, but the leaf might differ
    // from what the FE picks as active) and every subsequent branch switch.
    useContextBudgetStore.getState().refresh(threadId, leafId);
  }, [threadId, leafId]);

  if (!snap) return null;

  const percent = Math.max(0, Math.min(100, snap.percent));
  const dashOffset = CIRCUMFERENCE - (CIRCUMFERENCE * percent) / 100;
  // Extended-context mode wins the colour choice. Backend forces snap.trimmed = false in
  // extended mode, and we deliberately suppress the percent-based amber override too —
  // there is nothing to warn about when no compaction is going to happen.
  const extended = snap.extendedContextEnabled;
  const trimmed = !extended && (snap.trimmed || percent >= 75);
  const ringStroke = extended
    ? "rgb(20 184 166 / 0.9)"      // teal-500
    : trimmed
      ? "rgb(245 158 11 / 0.9)"   // amber-500
      : "currentColor";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={t("thread.contextDonut.label", { percent })}
          className="aui-context-budget-donut shrink-0 rounded-full p-0.5 hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden>
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              stroke="currentColor"
              strokeOpacity={0.2}
              strokeWidth={STROKE}
              fill="none"
            />
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              stroke={ringStroke}
              strokeWidth={STROKE}
              fill="none"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              strokeLinecap="round"
              transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
            />
          </svg>
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">
        <div className="text-xs leading-snug">
          <div>{t("thread.contextDonut.label", { percent })}</div>
          <div className="font-mono opacity-70">
            {t("thread.contextDonut.tokens", {
              used: snap.tokens.toLocaleString(),
              budget: snap.budget.toLocaleString(),
            })}
          </div>
          {extended && <div className="text-teal-500">{t("thread.contextDonut.extended")}</div>}
          {trimmed && <div className="text-amber-500">{t("thread.contextDonut.trimmed")}</div>}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};
